import React from "react";
import { imputationDocumentId } from "../pages/review-expense/utils/contract-expense-json";
import { findMatchingCategory, resolveCategoryDisplayLang } from "./xpens-categories";

// Single source of truth for the page size used by all paginated GET /imputations/ fetches.
export const IMPUTATION_FETCH_PAGE_SIZE = 500;

/** Alphabetical sort for GET /imputations/ (matches config table and displacement pickers). */
export const IMPUTATION_CATEGORY_SORTING = [{ id: "category", desc: false }];

/**
 * Build GET /imputations/ URL for review form and expense editor (for_review + optional owner scope).
 */
export function buildImputationsListUrl(filters, reviewForUserId, idClient) {
    const allFilters = idClient
        ? [{ id: "id_client", value: idClient }, ...filters]
        : filters;
    let url = `/imputations/?filters=${encodeURIComponent(JSON.stringify(allFilters))}&sorting=${encodeURIComponent(
        JSON.stringify(IMPUTATION_CATEGORY_SORTING)
    )}&start=${encodeURIComponent(0)}&page_size=${encodeURIComponent(IMPUTATION_FETCH_PAGE_SIZE)}&for_review=1`;
    if (reviewForUserId) {
        url += `&review_for_user_id=${encodeURIComponent(String(reviewForUserId))}`;
    }
    return url;
}

/**
 * Label shown in user-facing imputation dropdowns (review form, account editor).
 * Locked XPENS defaults (`default_category`) use official catalog names in
 * the app language. Firm-created names stay as stored. When `data` is omitted,
 * language is English.
 */
export function imputationDisplayLabel(row, data) {
    if (!row) return "";
    const lang = resolveCategoryDisplayLang(data);
    if (data && row.default_category === true) {
        const match =
            findMatchingCategory(row.category, data.xpensCategories) ||
            findMatchingCategory(row.category_translation, data.xpensCategories);
        if (match) {
            const official = lang === "fr" ? match.name_fr : match.name_en;
            if (official) return official;
        }
    }
    if (lang === "fr") {
        return row.category_translation || row.category || "";
    }
    return row.category || row.category_translation || "";
}

/** BCP 47 locale tag from app language code (`en` → `en-EN`, `fr` → `fr-FR`). */
export function imputationDisplayLabelLocale(lang) {
    const code = String(lang || "en").trim() || "en";
    return `${code}-${code.toUpperCase()}`;
}

export function compareImputationDisplayLabels(a, b, lang, data) {
    return imputationDisplayLabel(a, data).localeCompare(
        imputationDisplayLabel(b, data),
        imputationDisplayLabelLocale(lang),
        { sensitivity: "base" }
    );
}

/** Sort a copy of imputation rows by the label the user sees in dropdowns. */
export function sortImputationRowsByDisplayLabel(rows, lang, data) {
    return [...rows].sort((a, b) => compareImputationDisplayLabels(a, b, lang, data));
}

export function compareDisplayLabelStrings(labelA, labelB, lang) {
    return String(labelA ?? "").localeCompare(
        String(labelB ?? ""),
        imputationDisplayLabelLocale(lang),
        { sensitivity: "base" }
    );
}

/**
 * Normalize department_ids from API (string[] or ObjectId-like) to string[].
 * Used by every surface that displays or submits department assignments.
 *
 * @param {object} imputation
 * @returns {string[]}
 */
export function normalizeDepartmentIdsFromApi(imputation) {
    const raw = imputation?.department_ids;
    if (!Array.isArray(raw)) return [];
    return raw
        .map((x) => {
            if (x == null) return "";
            if (typeof x === "object" && x.$oid != null) return String(x.$oid);
            return String(x);
        })
        .filter(Boolean);
}

/**
 * Normalize rank_ids from API to positive integers (Aderant rank Id values are 1+).
 * Mirrors xpens-api ImputationsService._validate_rank_ids / _is_positive_int.
 *
 * @param {object} imputation
 * @returns {number[]}
 */
export function normalizeRankIdsFromApi(imputation) {
    const raw = imputation?.rank_ids;
    if (!Array.isArray(raw)) return [];
    const out = [];
    const seen = new Set();
    raw.forEach((x) => {
        if (typeof x !== "number" || !Number.isInteger(x) || x <= 0 || seen.has(x)) return;
        seen.add(x);
        out.push(x);
    });
    return out;
}

/** True when the imputation row is a child (has parent_id). */
export function imputationRowHasParentId(row) {
    const p = row?.parent_id;
    if (p == null || p === "") return false;
    return true;
}

/**
 * Display name for the imputations configuration table.
 * Locked XPENS defaults follow the app language. Firm-created rows prefer
 * `category` (the field administrators edit) over a stale translation.
 */
export function imputationConfigRowDisplayName(imputation, categoryLabel, data) {
    if (imputation?.default_category === true) {
        return imputationDisplayLabel(imputation, data) || (categoryLabel ? (categoryLabel(imputation?.category) || "") : "");
    }
    const category = (imputation?.category || "").trim();
    if (category) {
        return category;
    }
    const translation = (imputation?.category_translation || "").trim();
    if (translation) {
        return translation;
    }
    return categoryLabel ? (categoryLabel(imputation?.category) || "") : "";
}

/**
 * When saving from the config page, keep category_translation aligned with category
 * unless this is a Verify default row (bilingual EN/FR must not be overwritten).
 */
export function applyCategoryTranslationSync(payload, sourceRow = payload) {
    const next = { ...payload };
    if (sourceRow?.default_category === true) {
        return next;
    }
    const category = String(next.category ?? "").trim();
    if (!category) {
        return next;
    }
    next.category_translation = category;
    return next;
}

/** Map a child imputation from GET /imputations/ into modal sub-category state. */
export function mapApiChildToSubcategory(child) {
    return {
        _id: imputationDocumentId(child),
        category: child.category || "",
        gl_natural: child.gl_natural || "",
        gl_office: child.gl_office || "",
        cost_type: child.cost_type || "",
        tax_code: child.tax_code ?? null,
        maps_from_verify_category: child.maps_from_verify_category ?? null,
        default_expense_type: child.default_expense_type ?? null,
        client_filter: child.client_filter ?? null,
        approver_user_ids: child.approver_user_ids ?? [],
        department_ids: normalizeDepartmentIdsFromApi(child),
        rank_ids: normalizeRankIdsFromApi(child),
        ...(child.default_category === true ? { default_category: true } : {}),
    };
}

/**
 * Resolve which row to load in the edit modal when the user clicks a table cell.
 */

export function resolveImputationEditTarget(imputations, clickedRow, parentIds) {
    const clickedId = imputationDocumentId(clickedRow);
    if (!clickedRow || !clickedId) {
        return { parentRow: clickedRow, focusedChildId: null, isParentMode: false };
    }

    if (imputationRowHasParentId(clickedRow)) {
        const parentId = String(clickedRow.parent_id);
        const parentRow = (imputations || []).find((row) => imputationDocumentId(row) === parentId);
        if (parentRow) {
            return { parentRow, focusedChildId: clickedId, isParentMode: true };
        }
        return { parentRow: clickedRow, focusedChildId: null, isParentMode: false };
    }

    const isParent = parentIds instanceof Set && parentIds.has(clickedId);
    return {
        parentRow: clickedRow,
        focusedChildId: null,
        isParentMode: isParent,
    };
}

/**
 * GET /imputations/<id> for the review form. include_deleted lets the form
 * name a soft-deleted row already stored on the expense (XPEN-3304).
 */
export function buildImputationByIdUrl(imputationId, reviewForUserId, { includeDeleted = false } = {}) {
    const id = encodeURIComponent(String(imputationId));
    const params = new URLSearchParams();
    params.set("for_review", "1");
    if (includeDeleted) params.set("include_deleted", "1");
    if (reviewForUserId) params.set("review_for_user_id", String(reviewForUserId));
    return `/imputations/${id}?${params.toString()}`;
}

export function isSoftDeletedImputation(doc) {
    return Boolean(doc && doc._is_deleted === true);
}

export function deletedImputationOptionLabel(row, orphanSuffix, data) {
    const name = imputationDisplayLabel(row, data);
    const suffix = orphanSuffix || "deleted";
    return name ? `${name} (${suffix})` : `(${suffix})`;
}

/**
 * Render a deleted picker option as name + Bootstrap danger badge.
 * Detection uses option.isDeleted (or _is_deleted on option.value / option.row),
 * not the label suffix. Live options keep their plain label.
 */
export function formatImputationSelectOptionLabel(option, orphanSuffix) {
    if (!option) return null;
    const suffix = orphanSuffix || "deleted";
    const isDeleted = Boolean(
        option.isDeleted ||
        isSoftDeletedImputation(option.row) ||
        isSoftDeletedImputation(option.value)
    );
    if (!isDeleted) {
        return option.label;
    }
    const wrapped = `(${suffix})`;
    const marker = ` ${wrapped}`;
    const label = option.label == null ? "" : String(option.label);
    const name =
        label === wrapped ? "" : label.endsWith(marker) ? label.slice(0, -marker.length) : label;
    return (
        <span className="d-inline-flex align-items-center">
            {name ? (
                <>
                    <span>{name}</span>
                    <span className="mx-2">-</span>
                </>
            ) : null}
            <span
                className="badge badge-danger align-middle"
                style={{ fontSize: "0.8rem", padding: "0.3em 0.55em" }}
            >
                {suffix}
            </span>
        </span>
    );
}

/**
 * When a stored imputation id is missing from the live picker list, look it up
 * with include_deleted. Soft-deleted → keep and display. Live-but-hidden
 * (e.g. verify_default filtered out) or a successful empty body → clear.
 * HTTP error (404, network, 5xx) → do not clear: a 404 is also what a deleted
 * row returns if include_deleted is not yet deployed, so wiping would revive
 * the silent-blank bug. Callers must ignore action "error" for writes.
 */
export function resolveStoredImputationMissingFromList(
    { storedId, liveRows, reviewForUserId, fetchImpl },
    onResult
) {
    const id = storedId != null ? String(storedId).trim() : "";
    if (!id) {
        onResult({ action: "in-list" });
        return;
    }
    if ((liveRows || []).some((row) => imputationDocumentId(row) === id)) {
        onResult({ action: "in-list" });
        return;
    }
    if (typeof fetchImpl !== "function") {
        onResult({ action: "error" });
        return;
    }
    fetchImpl(
        buildImputationByIdUrl(id, reviewForUserId, { includeDeleted: true }),
        "GET",
        {},
        (response) => {
            const doc = response?.data;
            if (isSoftDeletedImputation(doc)) {
                onResult({ action: "deleted", row: doc });
                return;
            }
            onResult({ action: "clear" });
        },
        () => {
            onResult({ action: "error" });
        }
    );
}
